"use client";

import { useState, useMemo } from "react";
import Image from "next/image";
import { useTripData } from "@/lib/trip-data-context";
import { formatDay } from "@/lib/format";
import { TabLoading, EmptyState } from "@/components/trip/ui";
import type { Destination, TimelineItem } from "@/lib/types";

const typeIcons: Record<TimelineItem["type"], string> = {
  activity: "📍",
  drive: "🚗",
  flight: "✈️",
  meal: "🍽️",
  hotel: "🏠",
  tip: "💡",
};

const typeBulletColors: Record<TimelineItem["type"], string> = {
  activity: "var(--accent)",
  drive: "var(--drive-ink)",
  flight: "var(--flight-ink)",
  meal: "var(--green)",
  hotel: "var(--green)",
  tip: "var(--text3)",
};

function DestinationPills({ destinations }: { destinations: Destination[] }) {
  if (destinations.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
      {destinations.map((d) => (
        <a
          key={d.id}
          href={d.navigationUri}
          target="_blank"
          rel="noopener"
          onClick={(e) => e.stopPropagation()}
          style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "var(--card)", border: "1px solid var(--border)", padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 500, color: "var(--text2)", textDecoration: "none" }}
        >
          <span style={{ fontSize: 12, lineHeight: 1 }}>📍</span>
          {d.name}
        </a>
      ))}
    </div>
  );
}

function DayNotes({ items, destMap }: { items: TimelineItem[]; destMap: Map<string, Destination> }) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginBottom: 14, display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map((item) => {
          const linkedDests = item.destinationIds.map((id) => destMap.get(id)).filter(Boolean) as Destination[];
          return (
            <div key={item.id} style={{
              background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8,
              padding: "10px 14px", borderLeft: "3px solid var(--text3)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: item.description || linkedDests.length > 0 ? 4 : 0 }}>
                <span style={{ fontSize: 13, lineHeight: 1 }}>{typeIcons[item.type]}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text2)" }}>{item.title}</span>
              </div>
              {item.description && (
                <p style={{ fontSize: 13, lineHeight: 1.6, color: "var(--text2)", margin: 0 }}>{item.description}</p>
              )}
              <DestinationPills destinations={linkedDests} />
            </div>
          );
        })}
    </div>
  );
}

function TimelineEntry({ item, destMap, isLast }: { item: TimelineItem; destMap: Map<string, Destination>; isLast: boolean }) {
  const linkedDests = item.destinationIds.map((id) => destMap.get(id)).filter(Boolean) as Destination[];
  // after-anchored: time:null with sort.type === "after" — no explicit clock time
  const isAnchored = !item.time && item.sort?.type === "after";

  return (
    <div style={{ display: "grid", gridTemplateColumns: "44px 1fr", minHeight: 0 }}>
      {/* Rail column */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{
            width: isAnchored ? 8 : 12,
            height: isAnchored ? 8 : 12,
            borderRadius: "50%",
            background: isAnchored ? "var(--bg)" : typeBulletColors[item.type],
            border: isAnchored ? `2px solid ${typeBulletColors[item.type]}` : "2px solid var(--bg)",
            boxShadow: "0 0 0 2px var(--border)",
            flexShrink: 0, marginTop: 14, zIndex: 1,
          }} />
          {(item.time || item.endTime) && (
            <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--text3)", textAlign: "center", marginTop: 4, lineHeight: 1.4 }}>
              {item.time && <div>{item.time}</div>}
              {item.endTime && <div style={{ color: "var(--text4)" }}>{item.endTime}</div>}
            </div>
          )}
        </div>
        {!isLast && (
          <div style={{ width: 2, flex: 1, background: "var(--border)", marginTop: 4 }} />
        )}
      </div>

      {/* Card column — flex so the card stretches to at least the rail's
          height (dot + time labels); the grid row is sized by the taller
          column, so no DOM measurement is needed. */}
      <div style={{ display: "flex", flexDirection: "column", paddingBottom: isLast ? 0 : 10 }}>
        <div style={{
          background: "var(--card)",
          border: isAnchored ? "1px dashed var(--border)" : "1px solid var(--border)",
          borderRadius: 8,
          padding: "10px 14px", flex: "1 0 auto",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: item.description || linkedDests.length > 0 ? 4 : 0 }}>
            <span style={{ fontSize: 13, lineHeight: 1 }}>{typeIcons[item.type]}</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: isAnchored ? "var(--text2)" : "var(--text)" }}>{item.title}</span>
          </div>
          {item.description && (
            <p style={{ fontSize: 13, lineHeight: 1.6, color: "var(--text2)", margin: 0 }}>{item.description}</p>
          )}
          <DestinationPills destinations={linkedDests} />
        </div>
      </div>
    </div>
  );
}

function OptionGroup({ items, destMap, isLast }: { items: TimelineItem[]; destMap: Map<string, Destination>; isLast: boolean }) {
  const time = items.find((it) => it.time)?.time ?? null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "44px 1fr", minHeight: 0 }}>
      {/* Rail column */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{
            width: 14, height: 14, borderRadius: "50%",
            background: "var(--warn)", border: "2px solid var(--bg)",
            boxShadow: "0 0 0 2px var(--border)",
            flexShrink: 0, marginTop: 14, zIndex: 1,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 8, color: "#fff", fontWeight: 700,
          }}>?</div>
          {time && (
            <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--text3)", textAlign: "center", marginTop: 4 }}>
              {time}
            </div>
          )}
        </div>
        {!isLast && (
          <div style={{ width: 2, flex: 1, background: "var(--border)", marginTop: 4 }} />
        )}
      </div>

      {/* Card column */}
      <div style={{ paddingBottom: isLast ? 0 : 10 }}>
        <div style={{
          padding: "2px 10px 4px", borderRadius: 4, fontSize: 10, fontWeight: 700,
          letterSpacing: "0.06em", fontFamily: "var(--mono)",
          background: "var(--warn-bg)", color: "var(--warn)", display: "inline-block",
          marginBottom: 6, textTransform: "uppercase",
        }}>
          Choose one
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((item, idx) => {
            const linkedDests = item.destinationIds.map((id) => destMap.get(id)).filter(Boolean) as Destination[];
            return (
              <div key={item.id} style={{
                background: "var(--card)", border: "1px dashed var(--border)", borderRadius: 8,
                padding: "10px 14px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: item.description || linkedDests.length > 0 ? 4 : 0 }}>
                  <span style={{ fontFamily: "var(--mono)", fontSize: 11, fontWeight: 700, color: "var(--text3)" }}>({idx + 1})</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{item.title}</span>
                </div>
                {item.description && (
                  <p style={{ fontSize: 13, lineHeight: 1.6, color: "var(--text2)", margin: 0 }}>{item.description}</p>
                )}
                <DestinationPills destinations={linkedDests} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TimelineRail({ items, destMap }: { items: TimelineItem[]; destMap: Map<string, Destination> }) {
  const dayNotes = items.filter((it) => it.sort?.type === "day-note");
  const timelineItems = items.filter((it) => it.sort?.type !== "day-note");

  let i = 0;
  const result: ({ type: "single"; item: TimelineItem } | { type: "options"; items: TimelineItem[] })[] = [];

  while (i < timelineItems.length) {
    const item = timelineItems[i];
    if (item.optionGroupId) {
      const groupItems: TimelineItem[] = [item];
      while (i + 1 < timelineItems.length && timelineItems[i + 1].optionGroupId === item.optionGroupId) {
        i++;
        groupItems.push(timelineItems[i]);
      }
      result.push({ type: "options", items: groupItems });
    } else {
      result.push({ type: "single", item });
    }
    i++;
  }

  return (
    <div>
      <DayNotes items={dayNotes} destMap={destMap} />
      <div style={{ marginBottom: 14 }}>
        {result.map((entry, idx) => {
          const isLast = idx === result.length - 1;
          if (entry.type === "options") {
            return <OptionGroup key={entry.items[0].id} items={entry.items} destMap={destMap} isLast={isLast} />;
          }
          return <TimelineEntry key={entry.item.id} item={entry.item} destMap={destMap} isLast={isLast} />;
        })}
      </div>
    </div>
  );
}


export default function ItineraryPage() {
  const { data, loading } = useTripData();

  // Today's day (if the trip is in progress) starts open. Derived, not an
  // effect: user interaction overrides it, everything else follows the data.
  const initialOpen = useMemo(() => {
    if (!data) return null;
    const today = new Date().toISOString().slice(0, 10);
    const idx = data.days.findIndex((d) => d.date === today);
    return new Set(idx >= 0 ? [idx] : []);
  }, [data]);
  const [openOverride, setOpenOverride] = useState<Set<number> | null>(null);
  const openDays = openOverride ?? initialOpen ?? new Set<number>();

  const toggle = (i: number) => {
    const next = new Set(openDays);
    next.has(i) ? next.delete(i) : next.add(i);
    setOpenOverride(next);
  };

  // Build lookup maps
  const destMap = useMemo(() => {
    if (!data) return new Map<string, Destination>();
    return new Map(data.destinations.map((d) => [d.id, d]));
  }, [data]);

  if (loading || !data) {
    return <TabLoading />;
  }

  const days = data.days;

  if (days.length === 0) {
    return <EmptyState>Day-by-day itinerary coming soon — still being planned.</EmptyState>;
  }

  const expandAll = () => setOpenOverride(new Set(days.map((_, i) => i)));
  const collapseAll = () => setOpenOverride(new Set());

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <button onClick={expandAll} style={{ background: "none", border: "1px solid var(--border)", borderRadius: 4, padding: "4px 12px", fontSize: 12, cursor: "pointer", color: "var(--text3)", fontFamily: "var(--mono)" }}>
          Expand all
        </button>
        <button onClick={collapseAll} style={{ background: "none", border: "1px solid var(--border)", borderRadius: 4, padding: "4px 12px", fontSize: 12, cursor: "pointer", color: "var(--text3)", fontFamily: "var(--mono)" }}>
          Collapse all
        </button>
      </div>

      {days.map((day, i) => {
        const isOpen = openDays.has(i);
        return (
          <div key={day.id} style={{ borderBottom: "1px solid var(--border)" }}>
            <div
              onClick={() => toggle(i)}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 0", flexWrap: "wrap", cursor: "pointer", userSelect: "none" }}
            >
              <div style={{ minWidth: 72, fontFamily: "var(--mono)", fontSize: 12, color: "var(--text3)", fontWeight: 500 }}>
                {formatDay(day.date)}
              </div>
              <div style={{ flex: 1, fontWeight: 600, fontSize: 15 }}>
                {day.title}
              </div>

              <span style={{ fontSize: 18, color: "var(--text3)", transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "none" }}>
                ▾
              </span>
            </div>

            {isOpen && (
              <div style={{ padding: "0 0 20px" }}>
                {day.heroImage && (
                  <div style={{ position: "relative", width: "100%", height: 200, marginBottom: 14, borderRadius: 8, overflow: "hidden" }}>
                    <Image src={day.heroImage.src} alt={day.heroImage.alt} fill style={{ objectFit: "cover" }} unoptimized />
                  </div>
                )}

                <TimelineRail items={day.timeline} destMap={destMap} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
